import type { CSSProperties } from 'react';

import styles from './FromBriefSection.module.css';
import { LandingImage } from './LandingImage';
import { PrimaryCta } from './PrimaryCta';

type FromBriefSectionProps = {
	onCta: () => void;
};

type Petal = {
	src: string;
	top: string;
	left: string;
	width: string;
	aspect: string;
	mTop: string;
	mLeft: string;
	mWidth: string;
};

type Label = {
	text: string;
	top: string;
	centerX: string;
	mTop: string;
	mLeft: string;
};

const PETALS: Petal[] = [
	{
		src: '/landing/brief-petal-6.svg',
		top: '63.636%',
		left: '-0.556%',
		width: '19.583%',
		aspect: '282 / 213',
		mTop: '33.14%',
		mLeft: '93.68%',
		mWidth: '27.10%',
	},
	{
		src: '/landing/brief-petal-1.svg',
		top: '69.639%',
		left: '18.889%',
		width: '21.215%',
		aspect: '305.5 / 169.5',
		mTop: '43.81%',
		mLeft: '78.47%',
		mWidth: '48.28%',
	},
	{
		src: '/landing/brief-petal-2.svg',
		top: '65.793%',
		left: '42.014%',
		width: '10.903%',
		aspect: '157 / 181.5',
		mTop: '54.94%',
		mLeft: '67.53%',
		mWidth: '15.53%',
	},
	{
		src: '/landing/brief-petal-3.svg',
		top: '54.371%',
		left: '55.556%',
		width: '17.014%',
		aspect: '245 / 235.5',
		mTop: '61.70%',
		mLeft: '42.88%',
		mWidth: '27.69%',
	},
	{
		src: '/landing/brief-petal-4.svg',
		top: '41.084%',
		left: '72.674%',
		width: '12.222%',
		aspect: '176 / 197',
		mTop: '69.99%',
		mLeft: '23.36%',
		mWidth: '20.74%',
	},
	{
		src: '/landing/brief-petal-5.svg',
		top: '21.154%',
		left: '84.132%',
		width: '15.208%',
		aspect: '219 / 235.5',
		mTop: '76.08%',
		mLeft: '-4.96%',
		mWidth: '28.32%',
	},
	{
		src: '/landing/brief-petal-7.svg',
		top: '5.245%',
		left: '94.722%',
		width: '10.521%',
		aspect: '151.5 / 155.5',
		mTop: '-100%',
		mLeft: '-100%',
		mWidth: '10%',
	},
];

const LABELS: Label[] = [
	{
		text: 'Monitors your business',
		top: '51.282%',
		centerX: '12.014%',
		mTop: '65.42%',
		mLeft: '1.33%',
	},
	{
		text: 'Assesses the gaps',
		top: '55.012%',
		centerX: '31.076%',
		mTop: '61.16%',
		mLeft: '21.60%',
	},
	{
		text: 'Builds the strategy',
		top: '52.098%',
		centerX: '46.632%',
		mTop: '54.19%',
		mLeft: '41.60%',
	},
	{
		text: 'Executes the plan',
		top: '42.657%',
		centerX: '60.625%',
		mTop: '48.00%',
		mLeft: '59.73%',
	},
	{
		text: 'Iterates on data',
		top: '29.604%',
		centerX: '75.278%',
		mTop: '40.39%',
		mLeft: '69.60%',
	},
	{
		text: 'Drives growth',
		top: '13.869%',
		centerX: '86.076%',
		mTop: '31.87%',
		mLeft: '80.00%',
	},
];

type CSSVars = CSSProperties & Record<`--${string}`, string>;

export const FromBriefSection = ({ onCta }: FromBriefSectionProps) => (
	<section className={styles.section}>
		<div className={styles.grid} aria-hidden="true" />
		<div className={styles.copyBlock}>
			<span className={styles.eyebrow}>From brief to launch</span>
			<h2 className={styles.heading}>
				Growth curve,
				<br />
				not learning curve.
			</h2>
			<p className={styles.sub}>
				A consultant who&rsquo;s grounded in your business: your customer data,
				your past campaigns, your client&rsquo;s brand voice. Project Bridgeway
				recommends a plan, then executes it.
			</p>
			<div className={styles.cta}>
				<PrimaryCta
					onClick={onCta}
					variant="light"
					label="Try Project Bridgeway"
				/>
			</div>
		</div>
		<div className={styles.curve} aria-hidden="true">
			{PETALS.map((p) => (
				<div
					key={p.src}
					className={styles.petalSlot}
					style={
						{
							'--top': p.top,
							'--left': p.left,
							'--width': p.width,
							'--aspect': p.aspect,
							'--mt': p.mTop,
							'--ml': p.mLeft,
							'--mw': p.mWidth,
						} as CSSVars
					}
				>
					<LandingImage src={p.src} alt="" className={styles.petalImg} />
				</div>
			))}
			{LABELS.map((l) => (
				<div
					key={l.text}
					className={styles.label}
					style={
						{
							'--top': l.top,
							'--cx': l.centerX,
							'--mt': l.mTop,
							'--ml': l.mLeft,
						} as CSSVars
					}
				>
					<span className={styles.labelPill}>{l.text}</span>
					<span className={styles.labelLine} />
				</div>
			))}
		</div>
	</section>
);
